import { useState } from 'react';
import { CheckCircle2, ClipboardCopy, FileArchive, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import {
  GZIP_INPUT_CAP,
  base64ToGzipString,
  gzipToBase64,
} from '../lib/gzipString';

const SAMPLE_INPUT =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit. ' +
  'Curabitur convallis, lacus eu egestas vehicula, ipsum eros volutpat sem.\n' +
  '中文 emoji 😀 — gzip 是一个非常常见的 HTTP 内容编码方式。';

type Direction = 'encode' | 'decode';

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / 1024 / 1024).toFixed(2)} MiB`;
}

/**
 * `/tools/encode/gzip` — text ↔ gzip+base64 codec via the browser's
 * native CompressionStream API.
 *
 * Two tabs:
 *   - 文本 → gzip+base64 — shows compression ratio + sizes
 *   - gzip+base64 → 文本 — auto-strips data: URI prefix
 *
 * No JS-side gzip; older browsers get a friendly error pointing at
 * the minimum supported versions instead of dragging in pako.
 */
export default function GzipTool(): JSX.Element {
  const [dir, setDir] = useState<Direction>('encode');
  const [input, setInput] = useState<string>(SAMPLE_INPUT);
  const [output, setOutput] = useState<string>('');
  const [stats, setStats] = useState<{
    in: number;
    out: number;
    ratio: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const onRun = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setStats(null);
    try {
      if (dir === 'encode') {
        const r = await gzipToBase64(input);
        setOutput(r.base64);
        setStats({ in: r.in, out: r.out, ratio: r.ratio });
      } else {
        const r = await base64ToGzipString(input);
        setOutput(r);
        setStats(null);
      }
    } catch (e) {
      setOutput('');
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async (): Promise<void> => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      /* ignore */
    }
  };

  const onSwitch = (d: Direction): void => {
    if (d === dir) return;
    setDir(d);
    setOutput('');
    setStats(null);
    setError(null);
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="gzip"
        category="encode"
        description={`gzip 压缩 / 解压（base64 输出）。基于浏览器原生 CompressionStream，需要 Chrome 80+ / Firefox 113+ / Safari 16.4+。输入上限 ${fmtBytes(
          GZIP_INPUT_CAP,
        )}。`}
      />

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            方向
          </span>
        </header>
        <div className="flex gap-1.5">
          <DirBtn active={dir === 'encode'} onClick={() => onSwitch('encode')}>
            文本 → gzip+base64
          </DirBtn>
          <DirBtn active={dir === 'decode'} onClick={() => onSwitch('decode')}>
            gzip+base64 → 文本
          </DirBtn>
        </div>
      </section>

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            输入
          </span>
          <span className="text-[11px] text-ink-dim">{input.length} 字符</span>
        </header>
        <textarea
          className="input min-h-[140px] resize-y font-mono text-[13px]"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          spellCheck={false}
          placeholder={
            dir === 'encode'
              ? '粘贴或输入要压缩的文本'
              : '粘贴 gzip+base64（支持 data:application/gzip;base64,XXX 前缀）'
          }
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRun}
            disabled={busy || !input}
            className="btn"
          >
            {busy ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                处理中…
              </>
            ) : (
              <>
                <FileArchive className="h-3.5 w-3.5" />
                {dir === 'encode' ? '压缩' : '解压'}
              </>
            )}
          </button>
        </div>
      </section>

      {error && (
        <div className="card-pad border-red-500/30 bg-red-500/5 text-[12px] text-red-500">
          {error}
        </div>
      )}

      <section className="card-pad flex min-h-0 flex-1 flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            输出
          </span>
          <div className="flex items-center gap-2 text-[11px] text-ink-dim">
            {stats && (
              <>
                <span className="badge font-mono">
                  in {fmtBytes(stats.in)}
                </span>
                <span className="badge font-mono">
                  out {fmtBytes(stats.out)}
                </span>
                <span
                  className={clsx(
                    'badge font-mono',
                    stats.ratio < 1
                      ? 'border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
                      : 'border-amber-500/30 text-amber-600 dark:text-amber-400',
                  )}
                >
                  ratio {(stats.ratio * 100).toFixed(1)}%
                </span>
              </>
            )}
            <button
              type="button"
              onClick={onCopy}
              disabled={!output}
              className="btn-ghost px-2 py-0.5"
              title="复制"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  已复制
                </>
              ) : (
                <>
                  <ClipboardCopy className="h-3 w-3" />
                  复制
                </>
              )}
            </button>
          </div>
        </header>
        <textarea
          className="input flex-1 resize-none font-mono text-[13px]"
          value={output}
          readOnly
          spellCheck={false}
          placeholder={busy ? '处理中…' : '点击按钮后在这里显示结果'}
        />
      </section>
    </div>
  );
}

function DirBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'rounded border px-3 py-1 text-[12px] transition',
        active
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-line text-ink-muted hover:border-accent/40 hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
